import React, { ReactElement } from 'react';
import './Searchbar.css';

import MultisearchItem from './MultisearchItem';
import Dialog from '@mui/material/Dialog';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import { Button, DialogTitle, InputAdornment, TextField } from '@mui/material';
import { Person, PersonFileInfo, NameIndices, Sex } from 'lib/Types';
import SearchIcon from '@mui/icons-material/Search';
import translationsJson from 'data/translations.json';
import { MobileDeviceContext } from 'lib/Screen';

const translations: Translations = translationsJson;

interface Cursor {
    index: number;
    value: string;
    originalValue: string;
}

interface Props {
    isMultiSearch?: boolean;
    placeholder: string;
    inputData: NameIndices;
    onSearch(searchedPerson: PersonFileInfo, searchedPeople?: PersonFileInfo[]): void;
    onItemRemoved?(searchedPerson: PersonFileInfo, searchedPeople?: PersonFileInfo[]): void;
    presetValues?: Person[];
}

interface State {
    similarResults: string[];
    inputValue: Person;
    fileId: number;
    multisearchValues: PersonFileInfo[];
    askSex: boolean;
    cursor: Cursor | undefined;
}

class Searchbar extends React.Component<Props, State> {
    static contextType = MobileDeviceContext;

    private searchableValues: string[];
    private searchedValue: string;
    private searchedSex: Sex | undefined;

    constructor(props: Props) {
        super(props);

        const state: State = {
            similarResults: [],
            inputValue: { name: "", sex: undefined },
            fileId: -1,
            multisearchValues: [],
            askSex: false,
            cursor: undefined
        };

        if (this.props.presetValues !== undefined && this.props.presetValues.length > 0) {
            if (this.props.isMultiSearch) {
                const multisearchValues: PersonFileInfo[] = []
                for (let value of this.props.presetValues) {
                    multisearchValues.push(this.createPersonFileInfo(value))
                }

                state.multisearchValues = multisearchValues;
            } else {
                state.inputValue = this.props.presetValues[0];
            }
        }

        this.state = state;

        this.searchableValues = Object.keys(this.props.inputData);
        this.searchedValue = "";
        this.searchedSex = undefined;

        this.onItemRemoved = this.onItemRemoved.bind(this);
    }

    private onSearch(value: string, sex?: Sex) {
        //console.log("Searching: ", value);
        value = value.toUpperCase();
        if (value.length === 0 || this.props.inputData[value] === undefined)
            return;

        let fileId = -1;
        const nameIndex = this.props.inputData[value];
        if (typeof nameIndex === "number") {
            fileId = nameIndex;
        } else {
            if (nameIndex.m !== undefined && nameIndex.f !== undefined) {
                if (sex === undefined) {
                    this.setState({ inputValue: { name: value, sex: undefined }, fileId: fileId, askSex: true });
                    return;
                }

                fileId = sex === Sex.Male ? nameIndex.m! : nameIndex.f!;
            } else if (nameIndex.m !== undefined) {
                sex = Sex.Male;
                fileId = nameIndex.m;
            } else {
                sex = Sex.Female;
                fileId = nameIndex.f!;
            }
        }

        if (this.searchedValue === value && this.searchedSex === sex) {
            this.setState({ askSex: false });
            return;
        }

        const searchedPerson: PersonFileInfo = { fileId: fileId, person: { name: value, sex: sex } };
        let searchedValues: PersonFileInfo[] = [];
        if (this.props.isMultiSearch) {
            searchedValues = [...this.state.multisearchValues];
            if (this.state.multisearchValues.find(val => val.person.name === value && val.person.sex === sex) === undefined)
                searchedValues.push(searchedPerson);
        }

        this.searchedValue = value;
        this.setState({ inputValue: this.props.isMultiSearch ? { name: '', sex: undefined } : { name: value, sex: sex }, similarResults: [], multisearchValues: searchedValues, askSex: false });
        this.props.onSearch(searchedPerson, searchedValues);
    }

    private async onKeyPress(event: React.KeyboardEvent<HTMLDivElement>) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.onSearch(this.state.inputValue.name);
            return;
        }
        if (event.key === 'Tab') {
            event.preventDefault();
            this.onChange(this.state.inputValue.name, true);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const index = this.state.cursor ? this.state.cursor.index + 1 : 0;
            const input = (this.state.cursor ? this.state.cursor.originalValue : this.state.inputValue.name).trim().toUpperCase();
            const similar = this.searchableValues.filter(val => val.includes(input));
            if (index === similar.length)
                return;

            const sorted = await this.sort(similar, input);
            const value = sorted.at(index) ? sorted[index] : input;
            if (this.state.cursor)
                this.setState({ cursor: { index: index, value: value, originalValue: this.state.cursor.originalValue }, inputValue: { name: value } });
            else
                this.setState({ cursor: { index: index, value: value, originalValue: this.state.inputValue.name }, inputValue: { name: value } });
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if(this.state.cursor === undefined)
                return;

            const index = this.state.cursor.index - 1;
            const input = this.state.cursor.originalValue.trim().toUpperCase();
            const similar = this.searchableValues.filter(val => val.includes(input));
            if (index === -1) {
                this.setState({ cursor: undefined, inputValue: { name: this.state.cursor.originalValue } })
                return;
            }

            const sorted = await this.sort(similar, input);
            const value = sorted.at(index) ? sorted[index] : input;
            this.setState({ cursor: { index: index, value: value, originalValue: this.state.cursor.originalValue }, inputValue: { name: value } });
            return;
        }

        // Reset cursor
        this.setState({ cursor: undefined });
    }

    private async onChange(inputStr: string, takeFirstValue: boolean = false) {
        if (inputStr.length < 2) {
            if (inputStr.length === 0)
                this.setState({ similarResults: [] });
            this.setState({ inputValue: { name: inputStr } });
            return;
        }

        const input = inputStr.trimStart().toUpperCase();
        const similar = this.searchableValues.filter(val => val.includes(input));
        const sorted = await this.sort(similar, input);
        if (takeFirstValue) {
            this.setState({ similarResults: [], inputValue: { name: sorted.at(0) ? sorted[0] : inputStr } });
            return;
        }
        this.setState({ similarResults: sorted, inputValue: { name: inputStr } });
    }

    private async sort(data: string[], key: string) {
        return data.sort((a, b) => this.sortComparer(key, a, b));
    }

    private sortComparer(key: string, a: string, b: string): number {
        //a = a.toLowerCase();
        //b = b.toLowerCase();
        if (a === key && b !== key)
            return -1;
        if (a.startsWith(key) && !b.startsWith(key))
            return -1;
        if (!a.startsWith(key) && b.startsWith(key))
            return 1;
        if (a.startsWith(key) && b.startsWith(key) && a.length < b.length)
            return -1;
        if (a.includes(key) && !b.includes(key))
            return -1;
        if (!a.includes(key) && b.includes(key))
            return 1;
        if (a.includes(key) && b.includes(key) && a.length < b.length)
            return -1;
        return 0;
    }

    private onItemRemoved(person: PersonFileInfo) {
        const items = [...this.state.multisearchValues];
        const index = items.indexOf(person);
        if (index > -1) {
            this.searchedValue = "";
            items.splice(index, 1);
            this.setState({ multisearchValues: items });
            if (this.props.onItemRemoved)
                this.props.onItemRemoved(person, items);
        }
    }

    private dialogClose(sex: Sex | undefined) {
        if (sex !== undefined)
            this.onSearch(this.state.inputValue.name, sex)
        else
            this.setState({ askSex: false });
    }

    private createPersonFileInfo(person: Person): PersonFileInfo {
        const name = person.name;
        let fileId = -1;
        const nameIndex = this.props.inputData[name];
        if (typeof nameIndex === "number") {
            fileId = nameIndex;
        } else {
            fileId = person.sex === Sex.Male ? nameIndex.m! : nameIndex.f!;
        }

        return { fileId: fileId, person: person };
    }

    private isNewData(oldPerson: Person[] | undefined, newPerson: Person[] | undefined): boolean {
        if (oldPerson === undefined && newPerson === undefined)
            return false;
        if (newPerson === undefined)
            return false;
        if (oldPerson === undefined)
            return true;
        if (oldPerson === newPerson)
            return false;
        if (newPerson.length === 0)
            return false;
        if (oldPerson.length !== newPerson.length)
            return true;
        for (let i = 0; i < oldPerson.length; i++) {
            if (oldPerson[i].name !== newPerson[i].name || oldPerson[i].sex !== newPerson[i].sex)
                return true;
        }

        return false;
    }

    componentDidUpdate(prevProps: Props) {
        if (this.isNewData(prevProps.presetValues, this.props.presetValues)) {
            if (this.props.isMultiSearch) {
                const multisearchValues: PersonFileInfo[] = []
                for (let value of this.props.presetValues!) {
                    multisearchValues.push(this.createPersonFileInfo(value))
                }

                this.setState({ inputValue: { name: '', sex: undefined }, similarResults: [], multisearchValues: multisearchValues });
            } else {
                this.setState({ inputValue: this.props.presetValues![0], similarResults: [] });
            }
        }
    }

    render() {
        const searchItems: ReactElement<MultisearchItem>[] = [];
        if (this.props.isMultiSearch) {
            for (let val of this.state.multisearchValues) {
                searchItems.push(<MultisearchItem person={val} removeClicked={this.onItemRemoved} />)
            }
        }

        const cursorIndex = this.state.cursor ? this.state.cursor.index : -1;
        
        return (
            <div className="searchbar">
                {
                    searchItems.length > 0 && <div className='multisearch-item-container' key="multisearch-items">{searchItems}</div>
                }
                <div className={`searchbar-input-container ${this.context ? 'mobile' : ''}`} key="searchbar-container">
                    <TextField label={translations.search[window.lang]} placeholder={this.props.placeholder} spellCheck={false}
                        onKeyDown={e => this.onKeyPress(e)} onChange={e => this.onChange(e.target.value)} value={this.state.inputValue.name}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined" type="search" className='searchbar-input' key="searchbar-input"
                    />
                    {
                        this.state.similarResults.length ? (
                            <div className="searchbar-suggestions" key="searchbar-suggestions">
                                {
                                    this.state.similarResults.map((name, index) => (
                                        <div className={`searchbar-suggestions-item ${index === cursorIndex ? 'hover' : ''}`} key={name} onClick={() => this.onSearch(name)}>{name}</div>
                                    ))
                                }
                            </div>
                        ) : (<></>)
                    }
                </div>

                <Dialog onClose={() => this.dialogClose(undefined)} open={this.state.askSex}>
                    <DialogTitle>{translations.chooseSex[window.lang]}</DialogTitle>
                    <Button onClick={() => this.dialogClose(Sex.Male)}>{translations.chooseSexMale[window.lang]} <MaleIcon className='gender-symbol' /></Button>
                    <Button onClick={() => this.dialogClose(Sex.Female)}>{translations.chooseSexFemale[window.lang]} <FemaleIcon className='gender-symbol' /></Button>
                </Dialog>
            </div>
        );
    }
}

export default Searchbar;